import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useSheetsSync } from '../hooks/useSheetsSync';
import { useSheetsSettings } from '../hooks/useSheetsSettings';
import { countSelected, applyTabNameFormat } from '../utils/syncHelpers';
import SyncTargetBanner from './SyncTargetBanner';
import PreviewSummaryStrip from './PreviewSummaryStrip';
import PreviewRowsTable from './PreviewRowsTable';
import CommitOutcomeList from './CommitOutcomeList';

const SheetsSyncPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: settingsState } = useSheetsSettings();
  const { state, runPreview, toggleRow, selectAll, clearAll, commit } = useSheetsSync();

  const [confirming, setConfirming] = useState(false);

  // Spreadsheet selector — derived from settings
  const spreadsheets = useMemo(() => {
    if (settingsState.status !== 'ready' && settingsState.status !== 'save-error') return [];
    return settingsState.settings.spreadsheets;
  }, [settingsState]);

  const [selectedId, setSelectedId] = useState<string>('');

  // Auto-select when spreadsheets load
  const effectiveId = useMemo(() => {
    if (selectedId) return selectedId;
    return spreadsheets[0]?.id ?? '';
  }, [selectedId, spreadsheets]);

  const selectedSpreadsheet = spreadsheets.find((s) => s.id === effectiveId) ?? spreadsheets[0];

  const tabName = useMemo(() => {
    if (settingsState.status !== 'ready' && settingsState.status !== 'save-error') return '';
    return applyTabNameFormat(settingsState.settings.tab_name_format, new Date());
  }, [settingsState]);

  const handlePreview = () => {
    if (!effectiveId) return;
    runPreview(effectiveId);
  };

  const handleCommitConfirmed = async () => {
    setConfirming(false);
    await commit();
  };

  const selectionCounts = useMemo(() => {
    if (state.status !== 'preview-ready' && state.status !== 'commit-done') return null;
    return countSelected(state.preview.rows, state.selected);
  }, [state]);

  const isPreviewing = state.status === 'previewing';
  const isCommitting = state.status === 'committing';
  const hasPreview = state.status === 'preview-ready' || state.status === 'commit-done';
  const previewData = hasPreview ? state.preview : null;
  const selectedSet = hasPreview ? state.selected : new Set<number>();

  const noSpreadsheets = spreadsheets.length === 0 && settingsState.status !== 'loading';

  return (
    <div className="space-y-6 pb-16">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Google Sheets Sync</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Reconcile the current month's tab with the dashboard. Always preview before committing.
        </p>
      </div>

      {/* Settings loading */}
      {settingsState.status === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
          Loading settings…
        </div>
      )}

      {settingsState.status === 'load-error' && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
          {settingsState.message}
        </div>
      )}

      {/* No spreadsheets configured */}
      {noSpreadsheets && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              No spreadsheets configured
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              Add a spreadsheet in Settings before running a sync.
            </p>
          </div>
        </div>
      )}

      {/* Spreadsheet selector (only shown when more than one) */}
      {spreadsheets.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
            Spreadsheet
          </label>
          <select
            value={effectiveId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {spreadsheets.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Target banner */}
      {selectedSpreadsheet && tabName && (
        <SyncTargetBanner
          spreadsheetName={selectedSpreadsheet.name}
          spreadsheetId={selectedSpreadsheet.id}
          tabName={tabName}
        />
      )}

      {/* Preview error */}
      {state.status === 'error' && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
          <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">
              {state.phase === 'preview' ? 'Preview failed' : 'Commit failed'}
            </p>
            <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">{state.message}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handlePreview}
          disabled={isPreviewing || isCommitting || !effectiveId}
          className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
        >
          {isPreviewing ? (
            <>
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              Previewing…
            </>
          ) : (
            <>
              <ArrowPathIcon className="w-4 h-4" />
              {hasPreview ? 'Re-run Preview' : 'Preview Sync'}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => navigate('/admin/sheets-settings')}
          className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Settings
        </button>
      </div>

      {/* Preview results */}
      {previewData && (
        <>
          <PreviewSummaryStrip summary={previewData.summary} />

          <PreviewRowsTable
            rows={previewData.rows}
            selected={selectedSet}
            onToggleRow={toggleRow}
            onSelectAll={selectAll}
            onClearAll={clearAll}
          />

          {state.status === 'commit-done' && (
            <CommitOutcomeList
              result={state.result}
              onRunPreviewAgain={handlePreview}
            />
          )}

          {/* Confirmation panel */}
          {confirming && selectionCounts && (
            <div className="flex flex-col gap-3 p-4 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Confirm commit — {selectionCounts.total} row{selectionCounts.total !== 1 ? 's' : ''} will be written
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                    {[
                      selectionCounts.create > 0 && `${selectionCounts.create} created`,
                      selectionCounts.update > 0 && `${selectionCounts.update} updated`,
                      selectionCounts.link > 0 && `${selectionCounts.link} linked`,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    . This writes to both the database and the sheet.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCommitConfirmed}
                  className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors shadow-sm"
                >
                  Confirm &amp; Apply
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Apply button */}
          {!confirming && state.status !== 'commit-done' && (
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={
                  isCommitting ||
                  (state.status === 'preview-ready' && state.selected.size === 0)
                }
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
              >
                {isCommitting ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Applying…
                  </>
                ) : (
                  `Apply ${selectionCounts?.total ?? 0} change${(selectionCounts?.total ?? 0) !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SheetsSyncPage;
